import React from "react";
import classnames from "classnames/bind";
import { observer } from "mobx-react";

import LicensesList from "./LicensesList";
import UnlimitedLicenses from "./UnlimitedLicenses";
import LicenseGroup, { licenseOption } from "./LicenseGroup";
import GuaranteedPayment from "./GuaranteedPayment";
import LicensePaid from "./LicensePaid";
import RecurrentPaymentError from "./RecurrentPaymentError";
import FreeAccess from "./FreeAccess";

import { sendNavigationMetric } from "../../../../../utils/sendMetric";

import { useStore } from "../../../../../connect";

import styles from "../ProfileDropdown.scss";

const cx = classnames.bind(styles);

const getColor = (payedServices, user) => {
  // Блок лицензий окрашивается красным в следующих случаях:
  // - если включен guaranteed_payment. Это означает, что лицензия не оплачена, но есть срок за который это можно сделать
  // - если произошла ошибка оплаты по recurrent_payment. Автоплатеж не списался и состояние профиля сбросилось в бесплатный. Обработка в компоненте <FreeAccess />
  // - если по сроку действия лицензиям осталось не больше 10 дней
  // Блок окрашивается в зеленый:
  // - если есть лицензия из unlimitedLicenses. Они странные и идут особняком от лицензий из payed-services
  // - если срок действия лицензий из payed_services > 10 дней

  const remainingDays = !payedServices.regular.isEmpty
    ? payedServices.regular.remainingDays
    : -1;

  let color;
  if ((remainingDays < 10 && remainingDays >= 0) || user.guaranteed_payment) {
    color = "red";
  } else {
    // Условие здесь получится такое
    // (!payedServices.regular.isEmpty && remainingDays > 10) || !payedServices.unlimited.isEmpty
    color = "green";
  }

  return color;
};

const LicensesBlock = () => {
  const { profile, baseUrl } = useStore();

  if (
    profile.payedServices.regular.isEmpty &&
    profile.payedServices.unlimited.isEmpty &&
    !profile.user.guaranteed_payment
  ) {
    // Пользователь мог стать бесплатным в результате того,
    // что не прошел платеж по рекуррентному
    if (profile.user.recurrent_payment) {
      return <RecurrentPaymentError />;
    }

    return <FreeAccess />;
  }

  let afterSection;
  let billingLink;
  let hidePlusIcon = false;
  if (profile.user.guaranteed_payment) {
    afterSection = <GuaranteedPayment />;
    hidePlusIcon = true;
    billingLink = `https://billing.${baseUrl}/${
      profile.user.guaranteed_payment.invoice_pay_type_id === 1
        ? "invoice"
        : "kvitanciya"
    }?invoiceid=${
      profile.user.guaranteed_payment.invoice_id
    }&utm_source=header&utm_campaign=new_header`;
  } else if (!profile.payedServices.regular.isEmpty) {
    const remainingDays = profile.payedServices.regular.remainingDays;

    afterSection = <LicensePaid expired={remainingDays <= 10} />;
    billingLink = `https://billing.${baseUrl}/addinvoice?option=${
      remainingDays > 1 ? 2 : 1
    }&utm_source=header&utm_campaign=new_header&utm_content=header_addinvoice`;
  }

  const color = getColor(profile.payedServices, profile.user);

  return (
    <div className={cx("licenses-block", `color-${color}`)}>
      {!profile.payedServices.unlimited.isEmpty && <UnlimitedLicenses />}
      {(!profile.payedServices.regular.isEmpty ||
        profile.user.guaranteed_payment) && (
        <LicenseGroup
          refreshDisabled={profile.refreshLicenses.disabled}
          refreshLoading={profile.refreshLicenses.loading}
          refreshError={profile.refreshLicenses.error}
          refreshAction={profile.refreshLicenses.refresh}
          after={afterSection}
          href={billingLink}
          onClick={() => sendNavigationMetric("LICENSES_BLOCK")}
          hidePlusIcon={hidePlusIcon}
          option={
            profile.payedServices.regular.remainingDays <= 10
              ? licenseOption.EXTEND
              : licenseOption.ADD
          }
        >
          <LicensesList />
        </LicenseGroup>
      )}
    </div>
  );
};

export default observer(LicensesBlock);
